import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import {
  DEFAULT_BRAND,
  SITE_URL,
  absoluteAssetUrl,
} from "../config/brand";

function setMetaTag(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkRel(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applyPageMeta({
  title,
  description,
  image,
  path,
  type = "website",
  siteName,
}) {
  const canonicalUrl = path ? `${SITE_URL}${path}` : SITE_URL;
  const ogImage =
    absoluteAssetUrl(image) || absoluteAssetUrl(DEFAULT_BRAND.logo_long_url);

  if (title) document.title = title;
  if (description) setMetaTag("name", "description", description);
  if (siteName) setMetaTag("name", "author", siteName);
  if (title) setMetaTag("property", "og:title", title);
  if (description) setMetaTag("property", "og:description", description);
  setMetaTag("property", "og:type", type);
  if (path !== undefined) setMetaTag("property", "og:url", canonicalUrl);
  setMetaTag("property", "og:image", ogImage);
  if (siteName) setMetaTag("property", "og:image:alt", `Logo ${siteName}`);
  if (siteName) setMetaTag("property", "og:site_name", siteName);
  setMetaTag("property", "og:locale", "fr_FR");
  setMetaTag("name", "twitter:card", "summary_large_image");
  if (title) setMetaTag("name", "twitter:title", title);
  if (description) setMetaTag("name", "twitter:description", description);
  setMetaTag("name", "twitter:image", ogImage);
  if (path !== undefined) setLinkRel("canonical", canonicalUrl);
}

/**
 * Meta SEO d'une page. Restaure le titre/description marque au démontage.
 */
export function usePageMeta({ title, description, image, path, type = "website" }) {
  const { settings } = useSettings();
  const brandName = settings.brand_name || DEFAULT_BRAND.brand_name;
  const defaultDescription =
    settings.seo_description ||
    settings.hero_subtitle ||
    DEFAULT_BRAND.seo_description;
  const defaultImage =
    settings.logo_long_url ||
    settings.logo_url ||
    settings.hero_image_url ||
    DEFAULT_BRAND.logo_long_url;

  useEffect(() => {
    applyPageMeta({
      title,
      description: description || defaultDescription,
      image: image || defaultImage,
      path,
      type,
      siteName: brandName,
    });

    return () => {
      const homeTitle = `${brandName} — Robotique & Programmation pour Enfants`;
      applyPageMeta({
        title: homeTitle,
        description: defaultDescription,
        image: defaultImage,
        path: "/",
        siteName: brandName,
      });
    };
  }, [title, description, image, path, type, brandName, defaultDescription, defaultImage]);
}

/**
 * SEO global : favicon + identité marque.
 * Titre/description d'accueil uniquement sur `/` (les pages utilisent usePageMeta).
 */
export default function Seo() {
  const { settings, loading } = useSettings();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;

    const brandName = settings.brand_name || DEFAULT_BRAND.brand_name;
    const description =
      settings.seo_description ||
      settings.hero_subtitle ||
      DEFAULT_BRAND.seo_description;
    const ogImage =
      settings.logo_long_url ||
      settings.hero_image_url ||
      settings.logo_url ||
      DEFAULT_BRAND.logo_long_url;
    const favicon = settings.logo_url || DEFAULT_BRAND.logo_url;
    const favHref = absoluteAssetUrl(favicon) || favicon;

    setMetaTag("name", "author", brandName);
    setMetaTag("property", "og:site_name", brandName);
    setLinkRel("icon", favHref);
    setLinkRel("apple-touch-icon", favHref);

    if (location.pathname === "/") {
      applyPageMeta({
        title: `${brandName} — Robotique & Programmation pour Enfants`,
        description,
        image: ogImage,
        path: "/",
        siteName: brandName,
      });
    }
  }, [settings, loading, location.pathname]);

  return null;
}
